'use client';

import { create } from 'mutative';
import {
  Children,
  cloneElement,
  type CSSProperties,
  memo,
  type ReactElement,
  type UIEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import * as ReactDOM from 'react-dom';

import {
  type ArrowParams,
  calcTooltipPosition,
  getArrowParams,
  improveTooltipPlacement,
  placementToArrowParams,
  type Point,
  type TooltipAlign,
  type TooltipPlacement,
} from './tooltipPlacement';
import { type DetectType, Trigger } from './Trigger';

type TooltipControllerProps = {
  animation?: string;
  arrowColor?: string;
  autoPlacement?: boolean;
  children: ReactElement[];
  closeOnClick?: boolean;
  detect: DetectType;
  duration?: string;
  id?: string;
  placement?: TooltipPlacement;
  align?: TooltipAlign;
  properties?: string[];
  returnState?: (isOpen: boolean) => any;
  targetMargin?: Point; // Margin applied to target area calculation, allows to adjust tooltip offset relative to target
  timeOut?: number;
  timing?: string;
  triggerClose?: boolean;
  isOpen?: boolean;
};

type TooltipControllerState = {
  actualPlacement: TooltipPlacement;
  animate: boolean;
  arrowParams: ArrowParams | undefined;
  divStyle: CSSProperties;
  isOpen: boolean;
  timeOutID: number | undefined;
  trigger: boolean;
};

const EMPTY_ARRAY: string[] = [];

// Without Popover API support (older browsers, jsdom) a `popover` element would stay hidden.
const supportsPopover =
  typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype;

const getElementRect = (element: Element | undefined) => {
  if (!element) {
    return {
      height: 0,
      width: 0,
      x: 0,
      y: 0,
    };
  }

  const rect = element.getBoundingClientRect();
  if (rect) {
    const { height, left, top, width } = rect;
    return {
      height,
      width,
      x: left,
      y: top,
    };
  }

  return {
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  };
};

export const TooltipController = memo<TooltipControllerProps>(
  ({
    align = 'middle',
    animation,
    autoPlacement = true,
    children,
    closeOnClick,
    detect,
    duration,
    isOpen: isOpenProperty,
    placement = 'top',
    properties = EMPTY_ARRAY,
    returnState,
    targetMargin,
    timeOut,
    timing,
    triggerClose = false,
  }) => {
    const [state, setState] = useState<TooltipControllerState>({
      actualPlacement: 'top',
      animate: false,
      arrowParams: placementToArrowParams[placement],
      divStyle: {
        background: 'transparent',
        border: 0,
        bottom: 'auto',
        left: 0,
        margin: 0,
        overflow: 'visible',
        padding: 0,
        pointerEvents: detect === 'hover' ? 'none' : 'inherit',
        position: 'absolute',
        right: 'auto',
        top: 0,
        transform: '',
        transitionDuration: duration,
        transitionProperty: properties.join(' '),
        transitionTimingFunction: timing,
        zIndex: 1400, // TODO: get from theme
      },
      isOpen: false,
      timeOutID: null,
      trigger: triggerClose,
    });
    const tooltipRef = useRef<HTMLSpanElement | undefined>(null);
    const targetRef = useRef(null);
    const timeOutIDRef = useRef<NodeJS.Timeout | undefined>(null);
    const isOpen = isOpenProperty ?? state.isOpen;

    const { animate: stateAnimate, trigger: stateTrigger } = state;

    const clearCloseTimeOutFunction = useCallback(() => {
      if (timeOutIDRef.current) {
        clearTimeout(timeOutIDRef.current);
      }
    }, []);

    useEffect(() => {
      return clearCloseTimeOutFunction;
    }, [clearCloseTimeOutFunction]);

    const closeMenu = useCallback(() => {
      if (timeOut && detect !== 'hover-interact') {
        clearCloseTimeOutFunction();
      }

      if (!isOpen) {
        return;
      }

      // Turn off the animation > removes the specific animation class.
      if (animation) {
        // Return menu status.
        if (returnState) {
          setState((prevState) =>
            create(prevState, (draft) => {
              draft.animate = false;
            }),
          );
          returnState(false);
        } else {
          setState((prevState) =>
            create(prevState, (draft) => {
              draft.animate = false;
            }),
          );
        }

        // Remove pointer events from all active DIVs.
        if (tooltipRef.current) {
          tooltipRef.current.style.pointerEvents = 'none';
        }
      } else if (returnState) {
        setState((prevState) =>
          create(prevState, (draft) => {
            draft.isOpen = false;
          }),
        );
        returnState(false);
      } else {
        setState((prevState) =>
          create(prevState, (draft) => {
            draft.isOpen = false;
          }),
        );
      }
    }, [
      isOpen,
      timeOut,
      detect,
      animation,
      returnState,
      clearCloseTimeOutFunction,
    ]);

    const closeTimeOutFunction = useCallback(() => {
      // oxlint-disable-next-line react-doctor/effect-needs-cleanup -- handle is stored in timeOutIDRef and cleared by clearCloseTimeOutFunction
      const timeOutID = setTimeout(closeMenu, timeOut);
      timeOutIDRef.current = timeOutID;
    }, [closeMenu, timeOut]);

    const openMenu = useCallback(
      (e: Event) => {
        e.preventDefault();
        clearCloseTimeOutFunction();

        if (stateAnimate) {
          return;
        }

        if (isOpen) {
          return;
        }

        setState((prevState) =>
          create(prevState, (draft) => {
            draft.isOpen = true;
          }),
        );
        if (returnState) {
          returnState(true);
        }

        // Turn on the animation > adds the specific animation class.
        if (animation) {
          setTimeout(() => {
            setState((prevState) =>
              create(prevState, (draft) => {
                draft.animate = true;
              }),
            );
            if (tooltipRef.current) {
              // Add the pointer events to all active DIVs.
              tooltipRef.current.style.pointerEvents = 'auto';
            }
          }, 0);
        }
      },
      [stateAnimate, isOpen, animation, returnState, clearCloseTimeOutFunction],
    );

    const updatePosition = useCallback(() => {
      let actualPlacement = placement;
      if (!isOpen || !targetRef.current) {
        return;
      }

      const targetRect = getElementRect(targetRef.current);
      if (targetMargin) {
        targetRect.x -= targetMargin.x;
        targetRect.y -= targetMargin.y;
        targetRect.width += 2 * targetMargin.x;
        targetRect.height += 2 * targetMargin.y;
      }

      const tooltipRect = getElementRect(tooltipRef.current);
      const tooltipSize = {
        height: tooltipRect.height,
        width: tooltipRect.width,
      };

      const windowSize = {
        height: document.documentElement.clientHeight,
        width: document.documentElement.clientWidth,
      };

      if (autoPlacement) {
        actualPlacement = improveTooltipPlacement(
          targetRect,
          windowSize,
          tooltipSize,
          actualPlacement,
          align,
        );
      }

      const position = calcTooltipPosition(
        targetRect,
        tooltipSize,
        actualPlacement,
        align,
      );
      const arrowParams = getArrowParams(
        targetRect,
        position,
        tooltipSize,
        actualPlacement,
        align,
      );

      setState((prevState) =>
        create(prevState, (draft) => {
          draft.arrowParams = arrowParams;
          draft.actualPlacement = actualPlacement;
          // position is in viewport coordinates (getBoundingClientRect), but the
          // portal is absolutely positioned in the document — offset by the
          // window scroll so the tooltip stays anchored on window-scrolled pages
          draft.divStyle.transform = `translate(${position.x + globalThis.scrollX}px, ${position.y + globalThis.scrollY}px)`;
        }),
      );
    }, [align, autoPlacement, isOpen, placement, targetMargin]);

    // oxlint-disable-next-line react-doctor/effect-needs-cleanup -- listeners are toggled by this effect's own branches and removed in the else path
    useEffect(() => {
      if (triggerClose !== stateTrigger) {
        setState((prevState) =>
          create(prevState, (draft) => {
            draft.trigger = triggerClose;
          }),
        );
        closeMenu();
      }

      const timeout = setTimeout(() => {
        if (animation) {
          if (stateAnimate) {
            // oxlint-disable-next-line react-doctor/effect-needs-cleanup -- removed in the else branch on the next run
            globalThis.addEventListener('click', closeMenu);
            globalThis.addEventListener('touchend', closeMenu);
          } else {
            globalThis.removeEventListener('click', closeMenu);
            globalThis.removeEventListener('touchend', closeMenu);
          }
        } else if (isOpen) {
          if (closeOnClick) {
            globalThis.addEventListener('click', closeMenu);
            globalThis.addEventListener('touchend', closeMenu);
          }
        } else if (closeOnClick) {
          globalThis.removeEventListener('click', closeMenu);
          globalThis.removeEventListener('touchend', closeMenu);
        }
      }, 0);

      return () => {
        clearTimeout(timeout);
        globalThis.removeEventListener('click', closeMenu);
        globalThis.removeEventListener('touchend', closeMenu);
      };
    }, [
      isOpen,
      stateTrigger,
      stateAnimate,
      animation,
      closeMenu,
      triggerClose,
      detect,
      closeOnClick,
    ]);

    useEffect(() => {
      if (isOpen && timeOut && detect !== 'hover-interact') {
        closeTimeOutFunction();
      }
    }, [isOpen, closeMenu, timeOut, detect, closeTimeOutFunction]);

    useLayoutEffect(() => {
      updatePosition();
      const resizeObserver = new ResizeObserver(updatePosition);
      const target = tooltipRef.current;
      if (target) {
        resizeObserver.observe(target);
        return () => {
          resizeObserver.disconnect();
        };
      }
    }, [updatePosition]);

    const setTooltipElement = useCallback((element: HTMLSpanElement | null) => {
      tooltipRef.current = element ?? undefined;
      if (supportsPopover) element?.showPopover();
    }, []);

    const stopPropagation = useCallback((e: UIEvent<HTMLSpanElement>) => {
      e.stopPropagation();
    }, []);

    const inputChildren = Children.map(children, (child: any) => {
      if (child.type === Trigger) {
        return cloneElement(child, {
          closeMenu,
          detect,
          openMenu,
          ref: targetRef,
          timeOutFunc: closeTimeOutFunction,
        });
      }

      return (
        isOpen &&
        ReactDOM.createPortal(
          <span
            ref={setTooltipElement}
            // Manual popovers live in the top layer, so the tooltip stays above open popovers (e.g. overflow menus).
            popover={supportsPopover ? 'manual' : undefined}
            className={state.animate ? `${animation}` : 'animate-tooltip'}
            style={state.divStyle}
            onClick={closeOnClick ? undefined : stopPropagation}
            onMouseEnter={
              detect === 'hover-interact'
                ? clearCloseTimeOutFunction
                : undefined
            }
            onMouseLeave={
              detect === 'hover-interact' ? closeTimeOutFunction : undefined
            }
            onTouchEnd={closeOnClick ? undefined : stopPropagation}
          >
            {cloneElement(child, { arrowParams: state.arrowParams })}
          </span>,
          document.body,
        )
      );
    });

    return <>{inputChildren}</>;
  },
);
